import { motion } from "framer-motion";
import { Calendar, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import CalendlySection from "@/components/landing/CalendlySection";
import WhatsAppButton from "@/components/landing/WhatsAppButton";

const signals = [
  {
    title: "Trayectoria no es lo mismo que prestigio",
    body: `Tener 25 años de mercado es un activo. Pero no alcanza por sí solo.

Hay empresas con décadas de historia que siguen siendo percibidas como un proveedor más. Y hay otras que consiguieron transformar esos años en algo diferente: autoridad.

La diferencia está en qué hizo la empresa con todo lo acumulado durante ese tiempo. Clientes. Casos. Conocimiento. Relaciones. Resultados. Reputación. Experiencia.

Todo eso puede existir dentro de una compañía sin haberse convertido todavía en Capital de Marca.

Por eso la pregunta no es solamente: ¿Cuántos años tiene tu empresa?

Es: ¿Qué posición consiguió construir gracias a esos años?`,
  },
  {
    title: "Importa con quién te comparan",
    body: `Una empresa nunca es evaluada de manera aislada. El mercado la ubica dentro de un grupo, y ese grupo determina buena parte de la percepción.

Podés tener la capacidad de una empresa de primera línea y seguir siendo comparado con jugadores mucho más chicos. Podés tener una solución superior y entrar igual en una licitación donde todo termina definiéndose por precio. Podés haber crecido enormemente y seguir siendo percibido como la empresa que eras diez años atrás.

Ese es uno de los síntomas más claros de una brecha de posición.

Porque llega un momento en el crecimiento en el que ya no alcanza con preguntarse: “¿Cómo conseguimos más clientes?”

Aparece una pregunta anterior: “¿En qué categoría mental nos está colocando el mercado?”

Las empresas de mayor prestigio no solamente consiguen que las conozcan. Consiguen que las ubiquen diferente.`,
  },
  {
    title: "El prestigio también se transfiere",
    body: `Pensá en lo que sucede cuando una empresa empieza a trabajar con un cliente muy reconocido. O cuando su fundador es invitado a un espacio importante. Cuando participa de una institución relevante. Cuando aparece en un medio respetado por su industria. Cuando desarrolla una alianza con otra compañía de alto nivel.

Algo cambia. No necesariamente cambió el producto. Cambió el contexto desde el que el mercado interpreta a la empresa.

Las marcas no construyen prestigio únicamente hablando de sí mismas. También lo construyen por asociación.

Las empresas, personas, instituciones, conversaciones y espacios con los que una marca se vincula funcionan como señales.

Por eso dos compañías con capacidades similares pueden terminar ocupando lugares completamente diferentes en una misma industria. Una participa del sector. La otra empieza a formar parte de sus referentes.`,
  },
  {
    title: "Hay un momento en que la empresa deja de hablar solo de lo que vende",
    body: `Las empresas que ocupan posiciones fuertes dentro de una industria suelen tener algo en común: tienen una mirada.

No solamente venden. Interpretan lo que está pasando. Tienen criterio sobre el futuro de su categoría. Generan conocimiento. Sus líderes empiezan a ser consultados. Sus casos se convierten en referencia. Su experiencia empieza a tener valor más allá del producto o servicio que comercializan.

Eso produce un cambio importante.

La empresa deja de aparecer únicamente cuando alguien quiere comprar. Empieza a aparecer cuando alguien quiere entender el sector.

Y esa es otra posición.`,
  },
  {
    title: "Tu propio nombre también puede elevar el valor de la empresa",
    body: `Hay algo que vemos especialmente en compañías con muchos años de trayectoria.

A veces el empresario tiene más reputación que la propia marca. Lo conocen. Confían en él. Tiene relaciones construidas durante décadas. Su palabra tiene peso. Pero ese capital quedó vinculado a la persona y nunca terminó de transferirse estratégicamente hacia la compañía.

También puede suceder lo contrario: la empresa es conocida, pero su fundador permanece completamente fuera de la conversación de la industria.

Ninguna de las dos situaciones es necesariamente incorrecta. La pregunta es si fue una decisión estratégica o simplemente ocurrió así.

Porque el prestigio del empresario y el prestigio de la compañía pueden potenciarse mutuamente. Y cuando eso está bien dirigido, aparece algo muy valioso: el nombre del empresario abre puertas para la empresa y la empresa aumenta el peso del nombre del empresario.`,
  },
];

const reflectionQuestions = [
  "¿Tu empresa es reconocida por el nivel que realmente tiene?",
  "¿Está siendo comparada con los jugadores con los que querés competir?",
  "¿Su trayectoria aumentó su autoridad o simplemente aumentó su antigüedad?",
  "¿La posición de la marca acompañó el crecimiento del negocio?",
];

const GuiaPage = () => {
  const scrollToCalendly = () => {
    const el = document.getElementById("calendly");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <main className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 md:pt-44 md:pb-28 bg-[#470628] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#470628] via-[#600832] to-[#470628]" />
        <div className="container px-4 relative z-10 text-center max-w-4xl mx-auto">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-block text-[#FF9FBD] text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase mb-6"
          >
            Guía exclusiva para empresarios con trayectoria
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
          >
            Cómo asociar tu empresa al{" "}
            <span className="text-[#FF9FBD]">prestigio de tu sector</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.18 }}
            className="text-white/90 text-lg md:text-xl lg:text-2xl max-w-3xl mx-auto mb-4 font-medium"
          >
            Capital de Marca© para líderes y empresas con trayectoria decididas a escalar.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="text-white/60 text-base md:text-lg max-w-2xl mx-auto"
          >
            5 señales para saber si tu empresa está ocupando la posición que le corresponde.
          </motion.p>
        </div>
      </section>

      {/* Intro */}
      <section id="intro" className="py-16 md:py-24 bg-background">
        <div className="container px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="font-display text-xl md:text-2xl lg:text-3xl font-bold text-foreground leading-snug mb-6">
              Tu empresa puede ser muy buena y, aun así, estar mal ubicada.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Hay empresas que llevan 20 o 30 años creciendo. Tienen estructura, equipo, buenos clientes, conocimiento del negocio y relaciones construidas durante décadas. Pero cuando uno mira cómo son percibidas desde afuera, aparece una diferencia.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-gradient-to-b from-[#470628] to-[#600832] rounded-2xl p-8 md:p-10 text-white text-center shadow-xl"
          >
            <p className="text-white/90 text-lg md:text-xl leading-relaxed font-medium">
              La empresa es una cosa. La marca parece otra. Y esa diferencia importa.
            </p>
            <p className="text-white/70 mt-4 leading-relaxed">
              Porque el mercado no conoce todo lo que pasa puertas adentro. No ve automáticamente la calidad del equipo, las decisiones que tomaste durante años, los clientes que confían en ustedes, la capacidad operativa ni todo lo que tuvieron que construir para llegar hasta acá.
            </p>
            <p className="text-white/90 mt-6 font-display text-xl md:text-2xl font-bold">
              El mercado interpreta señales. Y con esas señales decide en qué lugar ubicar a tu empresa.
            </p>
            <p className="text-[#FF9FBD] mt-4 font-semibold">
              La pregunta es: ¿La está ubicando donde realmente corresponde?
            </p>
          </motion.div>
        </div>
      </section>

      {/* Signals */}
      <section id="senales" className="py-16 md:py-24 bg-muted/30">
        <div className="container px-4 max-w-3xl mx-auto">
          {signals.map((signal, i) => (
            <motion.article
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="mb-16 last:mb-0"
            >
              <div className="flex items-start gap-4 sm:gap-5">
                <span className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm sm:text-base">
                  {i + 1}
                </span>
                <div>
                  <h2 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-foreground mb-4 leading-snug">
                    {signal.title.toUpperCase()}
                  </h2>
                  <div className="text-muted-foreground leading-relaxed text-sm sm:text-base whitespace-pre-line">
                    {signal.body}
                  </div>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Uncomfortable Question */}
      <section className="py-16 md:py-24 bg-[#470628] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#470628] to-[#600832]" />
        <div className="container px-4 relative z-10 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-8 text-center">
               Lo que debés revisar
            </h2>
            <div className="text-white/80 text-base sm:text-lg leading-relaxed space-y-6">
              <p>
                Imaginemos dos empresas. Tienen capacidades similares. Buenos equipos. Buenos productos. Años de experiencia. Resultados.
              </p>
              <p>
                Pero una de ellas:
              </p>
              <ul className="space-y-3 pl-4">
                <li className="flex items-start gap-3">
                  <ArrowRight className="w-5 h-5 text-[#FF9FBD] flex-shrink-0 mt-1" />
                  <span>es invitada a las conversaciones importantes de su industria;</span>
                </li>
                <li className="flex items-start gap-3">
                  <ArrowRight className="w-5 h-5 text-[#FF9FBD] flex-shrink-0 mt-1" />
                  <span>trabaja con compañías que elevan su reputación;</span>
                </li>
                <li className="flex items-start gap-3">
                  <ArrowRight className="w-5 h-5 text-[#FF9FBD] flex-shrink-0 mt-1" />
                  <span>sus líderes son reconocidos;</span>
                </li>
                <li className="flex items-start gap-3">
                  <ArrowRight className="w-5 h-5 text-[#FF9FBD] flex-shrink-0 mt-1" />
                  <span>su experiencia se convierte en conocimiento;</span>
                </li>
                <li className="flex items-start gap-3">
                  <ArrowRight className="w-5 h-5 text-[#FF9FBD] flex-shrink-0 mt-1" />
                  <span>su marca transmite la dimensión que alcanzó el negocio;</span>
                </li>
                <li className="flex items-start gap-3">
                  <ArrowRight className="w-5 h-5 text-[#FF9FBD] flex-shrink-0 mt-1" />
                  <span>y el mercado la considera una referencia.</span>
                </li>
              </ul>
              <p>
                La otra simplemente sigue haciendo muy bien su trabajo.
              </p>
              <p className="font-display text-xl md:text-2xl font-bold text-white text-center py-4">
                ¿Cuál de las dos creés que va a capturar las mejores oportunidades durante los próximos diez años?
              </p>
              <p className="text-white/70">
                Ahí está la diferencia entre tener valor y conseguir que ese valor trabaje a favor de la empresa.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Where is your company today? */}
      <section id="donde-estas" className="py-16 md:py-24 bg-background">
        <div className="container px-4 max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4">
              ¿Dónde está hoy tu empresa?
            </h2>
            <p className="text-muted-foreground text-base md:text-lg">
              No hace falta que respondas un cuestionario. Solo pensá en estas cuatro preguntas:
            </p>
          </motion.div>

          <div className="grid gap-6">
            {reflectionQuestions.map((question, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="glass-card rounded-xl p-6 md:p-8"
              >
                <div className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                    {i + 1}
                  </span>
                  <p className="font-display text-lg md:text-xl font-semibold text-foreground leading-snug">
                    {question}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-muted-foreground text-center mt-10 leading-relaxed"
          >
            Si alguna respuesta te genera dudas, puede haber una brecha que vale la pena mirar. No necesariamente significa cambiar todo. Muchas veces significa identificar qué valor ya existe, ordenar las señales correctas y decidir qué posición queremos construir a partir de ahora.
          </motion.p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#470628] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#470628] to-[#600832]" />
        <div className="container px-4 relative z-10 text-center max-w-2xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-6 leading-tight"
          >
            Conversemos
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/80 text-base sm:text-lg leading-relaxed mb-8"
          >
            En CORESSEN trabajamos con empresarios y empresas con trayectoria para detectar la brecha entre el valor que construyeron y la posición que hoy ocupan en el mercado.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="text-white/70 text-base sm:text-lg leading-relaxed mb-10"
          >
            La Llamada de Claridad es una primera conversación para entender tu caso, el momento de la empresa y qué podría estar limitando hoy su Capital de Marca. Sin presentaciones eternas ni diagnósticos genéricos. Una conversación entre empresarios sobre dónde está hoy tu empresa y dónde podría estar.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Button
              variant="gold"
              size="xl"
              onClick={scrollToCalendly}
              className="text-base sm:text-lg px-8 sm:px-12"
            >
              <Calendar className="w-5 h-5 mr-2" />
              AGENDAR UNA LLAMADA DE CLARIDAD
            </Button>
          </motion.div>
        </div>
      </section>

      <CalendlySection />
      <Footer />
      <WhatsAppButton />
    </main>
  );
};

export default GuiaPage;
